import React, { useEffect, useState, useCallback } from 'react'
import { fetchCmsGalleryBySlug, getOptimizedImageUrl } from '../lib/cms'

export default function GallerySection({ gallerySlug, title: customTitle, subtitle }) {
  const [gallery, setGallery] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeIndex, setActiveIndex] = useState(null)
  const [touchStartX, setTouchStartX] = useState(null)

  useEffect(() => {
    let isMounted = true
    async function loadGallery() {
      setLoading(true)
      const data = await fetchCmsGalleryBySlug(gallerySlug)
      if (isMounted) {
        setGallery(data)
        setLoading(false)
      }
    }
    loadGallery()
    return () => {
      isMounted = false
    }
  }, [gallerySlug])

  const images = gallery?.images || []
  const displayTitle = customTitle || gallery?.title || 'Galleri'

  const handleNext = useCallback(() => {
    if (images.length === 0) return
    setActiveIndex(prev => (prev === null ? 0 : (prev + 1) % images.length))
  }, [images.length])

  const handlePrev = useCallback(() => {
    if (images.length === 0) return
    setActiveIndex(prev => (prev === null ? 0 : (prev - 1 + images.length) % images.length))
  }, [images.length])

  const handleClose = useCallback(() => {
    setActiveIndex(null)
  }, [])

  // Keyboard controls for lightbox
  useEffect(() => {
    if (activeIndex === null) return
    const onKeyDown = e => {
      if (e.key === 'Escape') handleClose()
      if (e.key === 'ArrowRight') handleNext()
      if (e.key === 'ArrowLeft') handlePrev()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [activeIndex, handleClose, handleNext, handlePrev])

  // Touch swipe support for mobile
  const handleTouchStart = e => {
    setTouchStartX(e.touches[0].clientX)
  }

  const handleTouchEnd = e => {
    if (touchStartX === null) return
    const diff = touchStartX - e.changedTouches[0].clientX
    if (diff > 50) {
      handleNext()
    } else if (diff < -50) {
      handlePrev()
    }
    setTouchStartX(null)
  }

  if (loading && !gallery) {
    return (
      <div className="py-12 text-center text-[#86878D]">
        <i className="fa-solid fa-spinner fa-spin text-2xl mb-2 text-[#E36C0A]" aria-hidden="true" />
        <p className="text-sm">Henter galleri fra CMS...</p>
      </div>
    )
  }

  if (images.length === 0) {
    return null
  }

  return (
    <section className="my-16">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#55565C] mb-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
            <span>Dokumentation & Arbejdsbilleder</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            {displayTitle}
          </h2>
          {subtitle && <p className="text-[#55565C] text-sm mt-1">{subtitle}</p>}
        </div>
        <span className="text-xs font-medium text-[#86878D]">
          Klik på et billede for at åbne i stor visning ({images.length} billeder)
        </span>
      </div>

      {/* Grid of gallery images */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {images.map((imgUrl, idx) => (
          <button
            key={imgUrl + idx}
            type="button"
            onClick={() => setActiveIndex(idx)}
            className="group relative aspect-4/3 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200/80 shadow-sm hover:shadow-md transition-all text-left focus:outline-none focus:ring-2 focus:ring-[#E36C0A]"
            aria-label={`Åbn billede ${idx + 1} af ${images.length} i stor visning`}
          >
            <img
              src={getOptimizedImageUrl(imgUrl, { width: 600, height: 450, quality: 80, fit: 'cover' })}
              alt={`${displayTitle} - billede ${idx + 1}`}
              loading="lazy"
              className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-[#18191D]/30 group-hover:bg-[#18191D]/50 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100 duration-200">
              <span className="inline-flex items-center gap-2 bg-white/95 text-slate-900 text-xs font-semibold px-3 py-2 rounded-full shadow-md">
                <i className="fa-solid fa-magnifying-glass-plus text-[#E36C0A]" aria-hidden="true" />
                <span>Se i stor visning</span>
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Full-screen Lightbox Modal */}
      {activeIndex !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Galleri forstørret visning"
          className="fixed inset-0 z-50 bg-[#18191D]/95 backdrop-blur-sm flex flex-col justify-between p-4 sm:p-6"
          onClick={handleClose}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Top Bar */}
          <div
            className="flex items-center justify-between text-white max-w-7xl w-full mx-auto pb-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-sm font-medium">
              <span className="text-white font-bold">{displayTitle}</span>
              <span className="text-[#86878D] ml-2">
                ({activeIndex + 1} / {images.length})
              </span>
            </div>

            <button
              type="button"
              onClick={handleClose}
              className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
              aria-label="Luk visning"
            >
              <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
            </button>
          </div>

          {/* Image Container with Prev / Next buttons */}
          <div
            className="relative flex-1 flex items-center justify-center max-w-7xl w-full mx-auto my-auto overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={handlePrev}
              className="absolute left-2 sm:left-4 z-10 p-3 rounded-full bg-slate-900/80 hover:bg-slate-900 text-white shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-amber-400"
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left text-lg" aria-hidden="true" />
            </button>

            <img
              src={getOptimizedImageUrl(images[activeIndex], { width: 1400, quality: 85 })}
              alt={`${displayTitle} - billede ${activeIndex + 1}`}
              className="max-h-[78vh] max-w-full object-contain rounded-xl shadow-2xl transition-transform"
            />

            <button
              type="button"
              onClick={handleNext}
              className="absolute right-2 sm:right-4 z-10 p-3 rounded-full bg-slate-900/80 hover:bg-slate-900 text-white shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-amber-400"
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right text-lg" aria-hidden="true" />
            </button>
          </div>

          {/* Bottom helper text */}
          <div
            className="text-center text-xs text-slate-400 max-w-7xl w-full mx-auto pt-4"
            onClick={e => e.stopPropagation()}
          >
            Brug piletaster eller swipe på mobil for at bladre. Klik udenfor for at lukke.
          </div>
        </div>
      )}
    </section>
  )
}
